// Absolute date / money formatting helpers.
//
// These collapse the inline `Date#toLocaleDateString` / `Intl.DateTimeFormat`
// / `Intl.NumberFormat` call sites scattered across billing, settings,
// campaigns, leads and relances. The output is intentionally **byte-identical**
// to the inlined calls they replace (several tests assert formatted strings):
//
//   - `value.toLocaleDateString(loc, opts)` is specified to be equivalent to
//     `new Intl.DateTimeFormat(loc, opts).format(value)`, and a bare
//     `toLocaleDateString()` is equivalent to `new Intl.DateTimeFormat()` (both
//     default to numeric year/month/day). So every migrated site keeps its
//     exact locale + options and produces the same string.
//
// Relative-time formatting lives separately in `formatRelativeTime.ts`.

export type FormatDateOptions = Intl.DateTimeFormatOptions & {
  /** Locale tag(s); omit for the runtime default (matches `toLocale*(undefined, …)`). */
  locale?: Intl.LocalesArgument;
};

export type FormatMoneyOptions = Intl.NumberFormatOptions & {
  locale?: Intl.LocalesArgument;
};

function toDate(value: Date | number | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Format an absolute date. Pass the same options the call site used; omit
 * `locale` to match the runtime default (as `toLocaleDateString(undefined, …)`).
 */
export function formatDate(value: Date | number | string, options?: FormatDateOptions): string {
  const { locale, ...rest } = options ?? {};
  return new Intl.DateTimeFormat(locale, rest).format(toDate(value));
}

/**
 * Format a numeric money value. Thin wrapper over `Intl.NumberFormat`; the
 * caller owns any fallback for invalid currency codes (the constructor can
 * throw a `RangeError`).
 */
export function formatMoney(value: number, options?: FormatMoneyOptions): string {
  const { locale, ...rest } = options ?? {};
  return new Intl.NumberFormat(locale, rest).format(value);
}
